import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Linking,
  StyleSheet,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Ionicons } from "@expo/vector-icons";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice, useScreenDimensions } from "../../_layout";

const BlockViewTutorialBanner = observer(
  ({
    urlPath,
    onSendFeedback,
    onClose,
    onAccessOld,
    marginTop = 0,
    marginBottom = 0,
  }) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useDevice();
    const { screenSize } = useScreenDimensions();
    const [isTutorialHovered, setIsTutorialHovered] = useState(false);
    const [isFeedbackHovered, setIsFeedbackHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);
    const [isLinkHovered, setIsLinkHovered] = useState(false);

    const handleOpenTutorial = () => {
      const isSpanish = i18n.language === "es";
      const languagePrefix = isSpanish ? "/es" : "";
      const url = `https://app.efortcoach.com/docs${languagePrefix}${urlPath}`;
      Linking.openURL(url).catch((err) =>
        console.error("Failed to open URL:", err)
      );
    };

    const isSmallScreen = screenSize === "small";

    return (
      <View
        style={[
          styles.container,
          { marginTop: marginTop, marginBottom: marginBottom },
        ]}
      >
        <LinearGradient
          colors={["rgba(244, 244, 244, 1)", "rgba(238, 238, 238, 1)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.gradient}
        >
          <TouchableOpacity
            style={[styles.closeButton, { opacity: isCloseHovered ? 0.5 : 1 }]}
            onPress={onClose}
            onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
            onMouseLeave={() => setIsCloseHovered(false)}
          >
            <Ionicons name="close" size={16} color={Colors.darkgrayColor} />
          </TouchableOpacity>
          <View
            style={[
              isSmallScreen
                ? styles.contentContainerColumn
                : GlobalStyles.HStack,
              styles.contentContainer,
            ]}
          >
            <View style={[GlobalStyles.HStack, styles.topRow]}>
              <View style={styles.iconContainer}>
                <Ionicons name="school" size={20} color={Colors.blackColor} />
              </View>
              <View style={styles.textContainer}>
                <Text style={styles.titleText} numberOfLines={3}>
                  {t("blockViewTutorialTitle-string")}
                </Text>
                <Text style={styles.inlineText}>
                  {t("blockViewOldViewHint-string")}{" "}
                  {onAccessOld && (
                    <Text
                      style={[
                        styles.linkText,
                        isLinkHovered && styles.linkTextHovered,
                      ]}
                      onPress={onAccessOld}
                      onMouseEnter={() => !isMobile && setIsLinkHovered(true)}
                      onMouseLeave={() => setIsLinkHovered(false)}
                    >
                      {t("blockViewOldViewLink-string")}
                    </Text>
                  )}
                  {"."}
                </Text>
              </View>
            </View>

            <View
              style={[
                GlobalStyles.HStack,
                styles.buttonsContainer,
                isSmallScreen && styles.buttonsContainerCentered,
                !isSmallScreen && { marginRight: 24 },
              ]}
            >
              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  { opacity: isTutorialHovered ? 0.8 : 1 },
                ]}
                onPress={handleOpenTutorial}
                onMouseEnter={() => !isMobile && setIsTutorialHovered(true)}
                onMouseLeave={() => setIsTutorialHovered(false)}
              >
                <Text style={styles.primaryText}>
                  {t("blockViewTutorialCta-string")}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.secondaryButton,
                  { opacity: isFeedbackHovered ? 0.8 : 1 },
                ]}
                onPress={onSendFeedback}
                onMouseEnter={() => !isMobile && setIsFeedbackHovered(true)}
                onMouseLeave={() => setIsFeedbackHovered(false)}
              >
                <Text style={styles.secondaryText}>
                  {t("blockViewFeedbackCta-string")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </LinearGradient>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {},
  gradient: {
    borderRadius: 14,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    position: "relative",
  },
  contentContainer: {
    padding: 16,
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
  },
  contentContainerColumn: {
    flexDirection: "column",
    alignItems: "stretch",
  },
  topRow: {
    flex: 1,
    alignItems: "center",
  },
  iconContainer: {
    marginRight: 12,
    width: 34,
    height: 34,
    backgroundColor: Colors.whiteColor,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 17,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  titleText: {
    fontSize: 14,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  inlineText: {
    fontSize: 12,
    color: Colors.darkgrayColor,
  },
  linkText: {
    color: Colors.bubbleColor,
    textDecorationLine: "underline",
  },
  linkTextHovered: {
    opacity: 0.8,
  },
  closeButton: {
    position: "absolute",
    top: 15,
    right: 15,
    zIndex: 10,
    padding: 2,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 12,
  },
  buttonsContainer: {
    gap: 8,
  },
  buttonsContainerCentered: {
    marginTop: 12,
    justifyContent: "center",
    width: "100%",
  },
  primaryButton: {
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  secondaryButton: {
    backgroundColor: "transparent",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(116, 116, 116, 1)",
  },
  secondaryText: {
    fontSize: 14,
    fontWeight: "400",
    color: "rgba(116, 116, 116, 1)",
  },
});

export default BlockViewTutorialBanner;
